import { Box, Text, Spinner, Center, Flex, Image } from '@chakra-ui/react';
import { formatSeconds } from '@halo/utils';
import { type GrabListRecords, type GroupMember, type SessionStore } from '@halo/chat-sdk-react';
import LukiestDraw from '@/assets/images/luckyBox/LukiestDraw.png';
import { LuckyType } from '../LuckyTabs';

type ClaimMembersType = GroupMember & GrabListRecords & { symbol: string };

export interface GrabInfo {
  claimedCount: number;
  finishedDuration: number;
  isClaimedEnd?: boolean;
  claimMembers?: ClaimMembersType[];
}

interface GrabListProps {
  sessionStore: SessionStore;
  payload?: Record<string, any>;
  onGrabInfo?: (value: GrabInfo) => void;
}
export function GrabList({ sessionStore, payload, onGrabInfo }: GrabListProps) {
  const { client } = sessionStore || {};
  const { t } = useTranslation();

  const [claimMembers, setClaimMembers] = useState<ClaimMembersType[]>([]);
  const [maxClaimAmount, setMaxClaimAmount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageInfo, setPageInfo] = useState({ page: 0, pageSize: 10 });
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const observerTarget = useRef<HTMLDivElement>(null);

  const [grabInfo, setGrabInfo] = useState<GrabInfo>({
    claimedCount: 0,
    finishedDuration: 0,
  });

  const isClaimedEnd = useMemo(() => {
    return grabInfo?.claimedCount === payload?.quantity;
  }, [grabInfo?.claimedCount, payload?.quantity]);

  const getIsMaxClaimAmount = useCallback(
    (amount: number | string) => {
      return (
        payload?.boxType === LuckyType.Random && isClaimedEnd && Number(amount) === maxClaimAmount
      );
    },
    [payload?.boxType, isClaimedEnd, maxClaimAmount]
  );

  const fetchListData = useCallback(async () => {
    if (!client?.Api && !payload?.packetNo) {
      return;
    }
    const {
      data: { records, finished_duration, claimed_count },
    } = await client.Api.luckyBox.getLuckyboxGrabList({
      packetno: payload!.packetNo,
      limit: pageInfo.pageSize,
      offset: pageInfo.page,
    });

    setGrabInfo({
      claimedCount: claimed_count || 0,
      finishedDuration: finished_duration || 0,
    });

    let currentMaxAmount = 0;
    const noMemberInfo: string[] = [];

    const claimMembers = await Promise.all(
      records?.map(async (grabMember) => {
        const member = await client.Api.user
          .getUsersInfo(grabMember.receiver_id)
          .catch(() => undefined);
        if (!member) {
          noMemberInfo.push(grabMember.receiver_id);
        }
        currentMaxAmount = Math.max(currentMaxAmount, Number(grabMember.amount));
        return {
          symbol: payload?.symbol,
          ...member,
          ...grabMember,
        };
      })
    );

    setMaxClaimAmount((prev) => Math.max(prev, currentMaxAmount));
    setClaimMembers((prev) => [...prev, ...(claimMembers as ClaimMembersType[])]);

    if (noMemberInfo.length) {
      const { removeListener } = client.Api.user.resolveUsersInfo(noMemberInfo, (users) => {
        setClaimMembers((prev: any[]) => {
          const prevs = prev.slice(0, pageInfo.page * pageInfo.pageSize);
          const newAddPrevs_ = prev.slice(pageInfo.page * pageInfo.pageSize);
          const newAddPrevs = newAddPrevs_?.map((grabMember) => {
            const user = users.find((user) => user?.user_id === grabMember.receiver_id);
            return user ? { ...grabMember, ...user } : grabMember;
          });
          return [...prevs, ...newAddPrevs];
        });
      });
      removeListener();
    }
    return { hasMore: pageInfo.page < Math.floor(claimed_count / pageInfo.pageSize) };
  }, [client?.Api, payload, pageInfo]);

  const loadMoreData = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      setError(null);
      const result = await fetchListData();
      setHasMore(result!.hasMore);
      setPageInfo((prev) => ({ ...pageInfo, page: prev.page + 1 }));
    } catch (_) {
      setError(t('errors.unknownNetworkError'));
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, fetchListData, pageInfo, t]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMoreData();
        }
      },
      { threshold: 0.1 }
    );

    const target = observerTarget.current;
    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [hasMore, loadMoreData, loadingMore]);

  useEffect(() => {
    onGrabInfo?.({
      ...grabInfo,
      isClaimedEnd,
      claimMembers,
    });
  }, [grabInfo, isClaimedEnd, claimMembers, onGrabInfo]);

  const list = claimMembers;

  return (
    <Box className="scrollHover" borderTop="1px solid" borderColor="grey.10">
      <Box minH="74px">
        {list.map((member, i) => {
          const isMaxClaimAmount = getIsMaxClaimAmount(member.amount);
          const isLast = i === list.length - 1;
          const isLukiestDraw = isMaxClaimAmount;

          return (
            <Flex
              mt="2"
              mb="2.5"
              px="3"
              key={member.user_id}
              gap="2.5"
              position="relative"
              borderBottom={isLast ? undefined : '1px solid'}
              borderColor={isLast ? undefined : 'grey.10'}
            >
              <Box flex="none">
                <Image src={member.avatar} w="9" h="9" borderRadius="full" />
                {isLukiestDraw && (
                  <Image
                    src={LukiestDraw}
                    w="52px"
                    h="52px"
                    borderRadius="full"
                    position="absolute"
                    left="1"
                    top="-2"
                  />
                )}
              </Box>
              <Flex
                direction="column"
                flex="1"
                justifyContent="center"
                gap="0.5"
                overflow="hidden"
              >
                <Flex minW={0} overflow="hidden">
                  <Text fontSize="sm" fontWeight="semibold" truncate minW={0} flexShrink={1}>
                    {member.name}
                  </Text>
                  {isLukiestDraw && (
                    <Text color="orange.100" fontSize="xs" fontWeight="medium" flexShrink="0">
                      {t('chat.lukiestDraw')}
                    </Text>
                  )}
                </Flex>
                <Text fontSize="xs" fontWeight="medium" color="fg.muted" truncate>
                  @{member.username}
                </Text>
              </Flex>
              <Box textAlign="right" flex="none">
                <Box
                  fontSize="sm"
                  fontWeight="semibold"
                  color={isLukiestDraw ? 'orange.100' : undefined}
                >
                  {member.amount} {member?.symbol}
                </Box>
                <Box fontSize="xs" fontWeight="medium" color="fg.muted">
                  {formatSeconds((member?.created_at || 0) * 1000)}
                </Box>
              </Box>
            </Flex>
          );
        })}
      </Box>
      {hasMore ? (
        <Box ref={observerTarget} py={4}>
          <Center>
            <Flex gap={2} alignItems="center">
              <Spinner size="sm" color="blue.500" />
              <Text fontSize="sm" color="gray.500">
                {t('chat.loading')}
              </Text>
            </Flex>
          </Center>
        </Box>
      ) : (
        list.length === 0 && (
          <Center py={4}>
            <Text color="gray.500" fontSize="sm">
              {error ? error : t('tips.noMoreData')}
            </Text>
          </Center>
        )
      )}
    </Box>
  );
}
